import { preferredLoginMethod, type LoginMethod } from './loginMethod';

// Getters, not a snapshot: read at module load these freeze whatever the env held
// at import time, which made the switches below disagree with themselves.
export const auth0Config = {
    get domain(): string | undefined {
        return import.meta.env.VITE_AUTH0_DOMAIN;
    },
    get clientId(): string | undefined {
        return import.meta.env.VITE_AUTH0_CLIENT_ID;
    },
    get audience(): string | undefined {
        return import.meta.env.VITE_AUTH0_AUDIENCE;
    },
};

export const AUTH0_CALLBACK_PATH = '/callback';

// The only route a sign-out may run from: it is public, so the member Layout —
// which redirects to /login the moment the session clears — is not mounted.
export const AUTH0_LOGOUT_PATH = '/logout';

// Must be registered in the tenant's Allowed Logout URLs (spa_logout_urls).
export const AUTH0_POST_LOGOUT_PATH = '/login';

// All three tenant values present. A half-configured build never redirects to a tenant
// that cannot issue a token. This decides whether the SDK is mounted at all, which an
// Auth0 session needs in order to be restored and ended whichever login a browser uses.
export function isAuth0Configured(): boolean {
    return Boolean(auth0Config.domain && auth0Config.clientId && auth0Config.audience);
}

// Whether /login sends this browser to Auth0. Collective's own login unless the
// environment makes Auth0 the default (VITE_AUTH0_ENABLED=true) or the browser opted in
// with /login?idp=auth0; /login?idp=collective opts it back out either way. Decides only how the next login
// happens -- a session that already exists is left to run, whichever kind it is. `override`
// is this visit's ?idp=, which /login stores only once it has rendered.
//
// An env flag plus the per-browser override, not a Statsig gate: the choice is made before
// sign-in, when there is no Statsig user to evaluate, and a build-time flag keeps a build
// without tenant values fully inert.
export function isAuth0Login(override?: LoginMethod | null): boolean {
    if (!isAuth0Configured()) {
        return false;
    }

    const preferred = override ?? preferredLoginMethod();
    if (preferred) {
        return preferred === 'auth0';
    }

    return import.meta.env.VITE_AUTH0_ENABLED === 'true';
}

export function auth0RedirectUri(): string {
    return `${window.location.origin}${AUTH0_CALLBACK_PATH}`;
}

export function auth0PostLogoutUri(): string {
    return `${window.location.origin}${AUTH0_POST_LOGOUT_PATH}`;
}

// The tenant's logout endpoint, built by hand for when the SDK is not there to build it.
export function auth0LogoutUrl(): string {
    const clientId = encodeURIComponent(auth0Config.clientId ?? '');
    const returnTo = encodeURIComponent(auth0PostLogoutUri());

    return `https://${auth0Config.domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
}
